import { z } from "zod";

import { type FormValidationOutput } from "./base";

// Extremely comprehensive list of disallowed usernames
const disallowedUsernames = [
  // System and administrative usernames
  "admin",
  "administrator",
  "root",
  "superuser",
  "sysadmin",
  "webmaster",
  "moderator",
  "mod",
  "owner",
  "staff",
  "team",
  "support",
  "helpdesk",
  "techsupport",
  "customerservice",
  "manager",
  "supervisor",
  "executive",
  "ceo",
  "cfo",
  "cto",
  "president",
  "vicepresident",
  "director",
  "coordinator",
  "specialist",
  "analyst",
  "consultant",
  "advisor",
  "expert",
  "iamtheadmin",
  "test_account",
  "supportteam",

  // Generic usernames
  "user",
  "guest",
  "test",
  "demo",
  "sample",
  "anonymous",
  "username",
  "name",
  "person",
  "human",
  "robot",
  "bot",
  "system",
  "default",
  "new",
  "old",
  "temp",
  "dummy",
  "fake",
  "real",
  "true",
  "false",
  "yes",
  "no",
  "maybe",
  "unknown",
  "hidden",
  "secret",
  "private",
  "public",
  "open",
  "closed",
  "locked",
  "unlocked",
  "enabled",
  "disabled",
  "active",
  "inactive",

  // Platform-specific terms
  "support",
  "help",
  "info",
  "contact",
  "sales",
  "billing",
  "account",
  "service",
  "api",
  "app",
  "system",
  "bot",
  "official",
  "verify",
  "verified",
  "real",
  "original",
  "genuine",
  "authentic",
  "premium",
  "pro",
  "plus",
  "basic",
  "standard",
  "enterprise",
  "business",
  "corporate",
  "personal",

  // Social media related
  "follower",
  "friend",
  "like",
  "share",
  "post",
  "tweet",
  "retweet",
  "comment",
  "subscribe",
  "unfollow",
  "block",
  "report",
  "trending",
  "viral",
  "influencer",
  "content",
  "creator",
  "stream",
  "live",
  "broadcast",
  "channel",
  "feed",
  "timeline",
  "profile",
  "bio",
  "status",
  "update",

  // Offensive or inappropriate terms (expanded, use with caution)
  "fuck",
  "fuck_you",
  "shit",
  "ass",
  "penis",
  "vagina",
  "pussy",
  "dick",
  "cock",
  "boob",
  "tit",
  "nude",
  "naked",
  "sex",
  "sexy",
  "hot",
  "xxx",
  "porn",
  "nsfw",
  "adult",
  "hentai",
  "bastard",
  "bitch",
  "cunt",
  "damn",
  "hell",
  "motherfucker",
  "bullshit",
  "asshole",
  "piss",
  "slut",
  "whore",
  "jerk",
  "douche",
  "turd",
  "prick",
  "anus",
  "butt",
  "booty",
  "nipple",
  "orgasm",
  "cum",
  "wank",
  "masturbate",
  "blowjob",
  "handjob",
  "dildo",
  "viagra",
  "horny",

  // Racial, ethnic, and religious slurs (use with extreme caution)
  "nigger",
  "nigga",
  "faggot",
  "fag",
  "retard",
  "spic",
  "kike",
  "chink",
  "gook",
  "wetback",
  "beaner",
  "gringo",
  "cracker",
  "honky",
  "kraut",
  "paki",
  "towelhead",
  "gyp",
  "gypsy",

  // Reserved for future use
  "api",
  "app",
  "system",
  "bot",
  "official",
  "dev",
  "test",
  "beta",
  "alpha",
  "gamma",
  "production",
  "staging",
  "development",
  "qa",
  "prototype",
  "pilot",
  "experimental",
  "release",
  "version",
  "update",
  "patch",
  "hotfix",
  "build",
  "compile",
  "debug",

  // Common service names (to prevent phishing)
  "paypal",
  "bank",
  "visa",
  "mastercard",
  "amex",
  "creditcard",
  "insurance",
  "healthcare",
  "government",
  "irs",
  "socialsecurity",
  "passport",
  "license",
  "driver",
  "police",
  "fbi",
  "cia",
  "nsa",
  "dhs",
  "army",
  "navy",
  "airforce",
  "marines",
  "coastguard",
  "military",
  "defense",

  // Technology companies and products (expanded)
  "facebook",
  "instagram",
  "whatsapp",
  "twitter",
  "tiktok",
  "snapchat",
  "linkedin",
  "youtube",
  "google",
  "microsoft",
  "apple",
  "amazon",
  "netflix",
  "spotify",
  "uber",
  "airbnb",
  "tesla",
  "oracle",
  "salesforce",
  "adobe",
  "ibm",
  "intel",
  "amd",
  "nvidia",
  "cisco",
  "dell",
  "hp",
  "lenovo",
  "asus",
  "acer",
  "samsung",
  "lg",
  "sony",
  "panasonic",
  "toshiba",
  "hitachi",
  "siemens",
  "philips",
  "nokia",
  "ericsson",
  "huawei",
  "xiaomi",
  "oppo",
  "vivo",
  "oneplus",

  // Gaming related (expanded)
  "minecraft",
  "fortnite",
  "roblox",
  "steam",
  "origin",
  "uplay",
  "playstation",
  "xbox",
  "nintendo",
  "blizzard",
  "activision",
  "ea",
  "epicgames",
  "rockstar",
  "ubisoft",
  "bethesda",
  "valve",
  "squareenix",
  "capcom",
  "konami",
  "sega",
  "bandainamco",
  "atari",
  "gamefreak",
  "supercell",

  // Cryptocurrency related (expanded)
  "bitcoin",
  "ethereum",
  "dogecoin",
  "crypto",
  "blockchain",
  "nft",
  "wallet",
  "exchange",
  "coinbase",
  "binance",
  "kraken",
  "gemini",
  "bitfinex",
  "poloniex",
  "bitstamp",
  "ledger",
  "trezor",
  "metamask",
  "uniswap",
  "defi",
  "ico",
  "token",
  "mining",
  "miner",
  "hash",

  // Common email providers
  "gmail",
  "hotmail",
  "yahoo",
  "outlook",
  "icloud",
  "protonmail",
  "mail",
  "email",
  "inbox",
  "aol",
  "zoho",
  "yandex",
  "gmx",
  "tutanota",
  "fastmail",
  "hushmail",
  "mailchimp",

  // Numbers and sequences
  "123",
  "abc",
  "qwerty",
  "password",
  "letmein",
  "admin123",
  "test123",
  "1234",
  "12345",
  "123456",
  "654321",
  "111111",
  "000000",
  "abcdef",
  "qwerty123",
  "iloveyou",
  "password1",
  "welcome",
  "monkey",

  // Additional disallowed usernames

  "admin2",
  "adminhelp",
  "supportstaff",
  "supporter",
  "support2",
  "teamofficial",
  "verifieduser",
  "troll",
  "spam",
  "spammer",
  "fakeaccount",
  "johnsmith",
  "johndoe",
  "janedoe",
  "elonmusk",
  "billgates",
  "timcook",
  "googlesupport",
  "applesupport",
  "appsupport",
  "phonenumber",
  "phone123",
  "emailuser",
  "officialteam",
  "livechat",
];

const usernameSchema = z
  .string()
  .min(4, "Username must be at least 4 characters long")
  .max(12, "Username must be at most 12 characters long")
  .regex(
    /^[a-zA-Z0-9_-]+$/,
    "Username can only contain letters, numbers, underscores, and hyphens",
  )
  .refine((value) => !disallowedUsernames.includes(value.toLowerCase()), {
    message: "This username is not allowed. Please choose a different one.",
  });

export function isValidUsername(username: string): FormValidationOutput {
  try {
    usernameSchema.parse(username);
    return { isValid: true, message: "" };
  } catch (e) {
    if (e instanceof z.ZodError) {
      return {
        isValid: false,
        message: e.issues[0]?.message ?? "Invalid username.",
      };
    }
    return { isValid: false, message: "Invalid username." };
  }
}
